import { useCallback, useEffect, useState, type ReactNode } from "react";

type SourceDescriptor = {
  srcSet: string;
  media?: string;
  type?: string;
};

type PhotoFigureProps = {
  src: string;
  alt: string;
  caption?: ReactNode;
  /** Klasy ramki (photo-frame jest dodawane automatycznie). */
  className?: string;
  /** Klasy dla samego obrazu. */
  imgClassName?: string;
  /** Element opakowujący obraz (np. wrapper parallax). */
  frameRef?: React.Ref<HTMLDivElement>;
  width?: number;
  height?: number;
  /** Responsywne źródła obrazu (format: "url1 768w, url2 1320w"). */
  srcSet?: string;
  /** Wzorce rozmiarów dla srcSet (np. "(max-width: 767px) 100vw, 50vw"). */
  sizes?: string;
  /** Źródła <picture> — gdy podane, przeglądarka wybiera pierwszy pasujący <source>. */
  pictureSources?: SourceDescriptor[];
  priority?: boolean;
  /** Klasy dla całej figury (razem z podpisem). */
  figureClassName?: string;
  /** Kliknięcie otwiera zdjęcie w powiększeniu. */
  zoomable?: boolean;
};

/**
 * Jednolita prezentacja zdjęć: miękka ramka (photo-frame), opcjonalny podpis
 * w małych kapitalikach i opcjonalne powiększenie w lekkim oknie (lightbox).
 */
export function PhotoFigure({
  src,
  alt,
  caption,
  className = "",
  imgClassName = "",
  frameRef,
  width,
  height,
  srcSet,
  sizes,
  pictureSources,
  priority = false,
  figureClassName = "",
  zoomable = true,
}: PhotoFigureProps) {
  const [open, setOpen] = useState(false);

  const image = (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      srcSet={srcSet}
      sizes={sizes}
      decoding="async"
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : undefined}
      className={imgClassName}
    />
  );

  const picture = pictureSources?.length ? (
    <picture>
      {pictureSources.map((s, i) => (
        <source key={i} srcSet={s.srcSet} media={s.media} type={s.type} />
      ))}
      {image}
    </picture>
  ) : (
    image
  );

  const frame = (
    <div className={`photo-frame ${className}`}>
      <div ref={frameRef} className="h-full">{picture}</div>
    </div>
  );

  return (
    <figure className={figureClassName}>
      {zoomable ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group block h-full w-full cursor-zoom-in text-left"
        >
          {frame}
          <span className="sr-only">Powiększ zdjęcie: {alt}</span>
        </button>
      ) : (
        frame
      )}
      {caption ? <figcaption className="photo-caption">{caption}</figcaption> : null}
      {zoomable ? (
        <Lightbox open={open} onClose={() => setOpen(false)} src={src} alt={alt} caption={caption} />
      ) : null}
    </figure>
  );
}

export function Lightbox({
  open,
  onClose,
  src,
  alt,
  caption,
}: {
  open: boolean;
  onClose: () => void;
  src: string;
  alt: string;
  caption?: ReactNode;
}) {
  const handleKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previous;
    };
  }, [open, handleKey]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/85 p-4 backdrop-blur-sm animate-fade-in md:p-10"
    >
      <div
        className="relative max-h-full w-full max-w-4xl"
        onClick={(event) => event.stopPropagation()}
      >
        <img
          src={src}
          alt={alt}
          className="mx-auto max-h-[80vh] w-auto max-w-full object-contain shadow-2xl"
        />
        {caption ? (
          <p className="photo-caption mt-4 text-center text-surface/80">{caption}</p>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-3 right-0 -translate-y-full border border-surface/40 px-3 py-1 text-xs uppercase tracking-[0.16em] text-surface transition-colors hover:bg-surface hover:text-ink"
        >
          Zamknij
        </button>
      </div>
    </div>
  );
}
